import { SKY_WORLD } from './86-sky-world-scale.mjs';
import { skyTerrainHeight } from './87-sky-terrain.mjs';
import { skyAuthoredPoseContact } from './115-sky-authored-pose.mjs';

const clamp = (v, low, high) => Math.max(low, Math.min(high, v));
const axis = (v) => (Number.isFinite(v) ? clamp(v, -1, 1) : 0);
const point = (state) => ({ x: state.x, y: state.y, z: state.pilotZ ?? SKY_WORLD.pilotZ });
const keys = ['x', 'y', 'z'];

/** Bride's real circular flight envelope. A/D changes arc length in metres;
 * W/S changes physical altitude. Radius is captured from the entry position,
 * never from the camera. The centre is the source carrier's settled root.
 * No time, score or position changes occur until the normal active tick calls it. */
export function stepSkyMourningOrbit(state, input, dt, { speed, hit }) {
  const boss = state.boss;
  if (!boss?.mourning || boss.age < 4) return false;
  const center = { x: 0, z: -32 + boss.authoredRig.bounds.min[2] };
  const orbit = (boss.mourning.orbit ??= {
    center,
    radius: Math.hypot(state.x - center.x, (state.pilotZ ?? 1) - center.z),
    angle: Math.atan2(state.x - center.x, (state.pilotZ ?? 1) - center.z),
    travelled: 0,
  });
  const wreck = boss.authoredProps.find((prop) => prop.mourningLamp);
  return stepSkyPhysicalOrbit(state, input, dt, {
    speed,
    hit,
    orbit,
    ceiling: boss.authoredFlightCeiling,
    solids: wreck ? [{ pose: wreck.authoredPose, reason: 'wreck' }] : [],
  });
}

/** Shared physical circular flight in world metres. Callers own the fixed
 * centre, radius and actual posed solids; this owns steering, source contact
 * and the single inward-facing basis used by pilot, weapons and camera. */
export function stepSkyPhysicalOrbit(
  state,
  input,
  dt,
  { speed, hit, orbit, ceiling, solids = [] },
) {
  const previous = point(state);
  const delta = (axis(input.x) * speed * dt) / orbit.radius;
  const angle = orbit.angle + delta;
  const candidate = {
    x: orbit.center.x + Math.sin(angle) * orbit.radius,
    y: state.y + axis(input.y) * 9 * dt,
    z: orbit.center.z + Math.cos(angle) * orbit.radius,
  };
  const floor =
    skyTerrainHeight(candidate.x, candidate.z, state.terrainTravel ?? 0) + SKY_WORLD.pilotAimHeight;
  candidate.y = clamp(candidate.y, floor, ceiling);
  const obstacle = solids.find(({ pose, meshes }) => {
    const bounds = pose?.bounds;
    const nearby =
      bounds &&
      keys.every(
        (key, i) =>
          Math.max(previous[key], candidate[key]) >= bounds.min[i] - SKY_WORLD.pilotContactRadius &&
          Math.min(previous[key], candidate[key]) <= bounds.max[i] + SKY_WORLD.pilotContactRadius,
      );
    return (
      nearby &&
      skyAuthoredPoseContact(pose, previous, candidate, meshes, SKY_WORLD.pilotContactRadius)
    );
  });
  if (obstacle) hit?.(state, obstacle.reason);
  else {
    state.x = candidate.x;
    state.y = candidate.y;
    state.pilotZ = candidate.z;
    orbit.angle = angle;
    orbit.travelled += Math.abs(delta) * orbit.radius;
  }
  const sin = Math.sin(orbit.angle),
    cos = Math.cos(orbit.angle);
  state.flightFrame = {
    origin: point(state),
    right: { x: cos, y: 0, z: -sin },
    up: { x: 0, y: 1, z: 0 },
    back: { x: sin, y: 0, z: cos },
  };
  state.flightAimDepth = 96;
  state.orbitalFlight = { sector: state.sector, worldId: state.boss.worldId };
  state.freeFlight = false;
  state.locomotion = 'fly';
  return true;
}

/** Sweep world-space hostile bolts against the moving pilot. A fixed world-Z
 * plane cannot represent a circular arena; relative motion prevents tunnelling.
 * Bolts retain finite active-time lifetimes regardless of their travel heading. */
export function stepSkyOrbitalThreat(state, bolt, dt, previousPilot, { hit }) {
  const from = { x: bolt.x, y: bolt.y, z: bolt.z };
  bolt.age = (bolt.age ?? 0) + dt;
  bolt.x += (bolt.vx ?? 0) * dt;
  bolt.y += (bolt.vy ?? 0) * dt;
  bolt.z += (bolt.vz ?? 24) * dt;
  const current = point(state),
    a = keys.map((key) => from[key] - previousPilot[key]),
    b = keys.map((key) => bolt[key] - current[key]),
    d = b.map((n, i) => n - a[i]),
    length = d.reduce((sum, n) => sum + n * n, 0),
    along = length > 0 ? clamp(-a.reduce((sum, n, i) => sum + n * d[i], 0) / length, 0, 1) : 0;
  if (
    Math.hypot(...a.map((n, i) => n + d[i] * along)) <
    SKY_WORLD.pilotContactRadius + (bolt.radius ?? 0)
  ) {
    bolt.spent = true;
    hit(state, 'projectile');
  }
}
